import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Navbar } from "./components/layout/Navbar";
import { SessionManager } from "./components/layout/SessionManager";
import { Dashboard } from "./pages/Dashboard";
import { Attendance } from "./pages/Attendance";
import { HealthMonitoring } from "./pages/HealthMonitoring";
import { SafetyReports } from "./pages/SafetyReports";
import { EmployeeManagement } from "./pages/EmployeeManagement";
import { Training } from "./pages/Training";
import { Profile } from "./pages/Profile";
import { Roles } from "./pages/Roles";
import { Departments } from "./pages/Departments";
import { Login } from "./components/layout/Login";
import { isAdmin } from "./lib/auth";

const isAuthenticated = () => {
  try {
    return localStorage.getItem("isLoggedIn") === "true";
  } catch {
    return false;
  }
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  return isAuthenticated() ? <>{children}</> : <Navigate to="/login" replace />;
};

const GuestRoute = ({ children }: { children: React.ReactNode }) => {
  return !isAuthenticated() ? <>{children}</> : <Navigate to="/" replace />;
};

const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  return isAdmin() ? <>{children}</> : <Navigate to="/" replace />;
};

const MainLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="flex">
      <Navbar />
      <main className="flex-1 min-h-screen lg:ml-56 transition-all duration-300">
        <div className="p-4 lg:pr-14 lg:pb-4 sm:pt-6 lg:pt-8">{children}</div>
      </main>
    </div>
  );
};

function App() {
  return (
    <Router>
      <SessionManager />
      <div className="min-h-screen bg-gray-50">
        <Routes>
          <Route
            path="/login"
            element={
              <GuestRoute>
                <Login />
              </GuestRoute>
            }
          />

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <Dashboard />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/attendance"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <Attendance />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/health"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <HealthMonitoring />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/safety-reports"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <SafetyReports />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/employees"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <EmployeeManagement />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/training"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <Training />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <Profile />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/roles"
            element={
              <AdminRoute>
                <MainLayout>
                  <Roles />
                </MainLayout>
              </AdminRoute>
            }
          />

          <Route
            path="/departments"
            element={
              <AdminRoute>
                <MainLayout>
                  <Departments />
                </MainLayout>
              </AdminRoute>
            }
          />

          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
